import { useCallback, useEffect, useMemo, useState } from 'react';
import { Handle, Position, useNodeId } from 'reactflow';
import * as Icons from "@mui/icons-material";

import '../FlowNode/flow-node.css'
import './render-node.css'

import { RenderNode as IRenderNode, Port, RenderData } from '../../peregrineFlowInterfaces';
import { Skeleton, Tooltip, Typography } from '@mui/material';

import useStore, { CustomReactFlowNode } from '../../store';
import { parseDataTypes, groupColors } from '../FlowNode/FlowNode';
import Plot from 'react-plotly.js';

export type IconNames = keyof typeof Icons // use this in other components

interface ImageDescription {
    width: number;
    height: number;
    channels: number;
}

interface Image {
    data: any;
    image_path: string;
    description: ImageDescription
}

const DataTypeSupportsFullscreen: Record<string, boolean> = {
    "Image": true,
    "str": false
} as const;

const RenderNode = (flowNode: any, isConnectable: boolean) => {
    const nodeId = useNodeId();
    const node = (flowNode as CustomReactFlowNode).data as IRenderNode;
    const [inputPorts, setInputPorts] = useState<Array<Port>>([]);
    const [renderData, setRenderData] = useState<RenderData>({ data_type: "", data: undefined } as RenderData)
    const [isFullscreen, setIsFullscreen] = useState(false);
    const edges = useStore((state) => state.edges);

    useEffect(() => {
        setInputPorts(node.input_ports);
        node.render_data = renderData;

        if (node.render_data) {
            // this might look strange but we have to update the whole node in reactflow without this approach
            // here, we just update the render data instead of iterating over all nodes, updating their data
            // re-render everything. This leads in the past to very much performance issues. Since this is not
            // very "reactive" it is a workaround for now.
            node.render_data.callback = (data: any) => {
                setRenderData(data as RenderData);
            }
        }
    }, [node, renderData, setRenderData])

    const color = useMemo(() => {
        return node.group in groupColors ? groupColors[node.group] : "var(--blue-one)";
    }, [node])

    const extraCssClasses = useMemo(() => {
        let css = "";

        if (renderData.data_type === "Plot") {
            css += " nowheel nodrag";
        }

        return css;
    },[renderData]);

    const handleFullscreen = useCallback((event: React.MouseEvent<HTMLElement>) => {
        if (event.detail < 2 || !renderData.data) {
            return;
        }

        if (!DataTypeSupportsFullscreen[renderData.data_type]) {
            return;
        }

        if (!isFullscreen) {
            (event.target as HTMLElement).requestFullscreen();
            setIsFullscreen(true);
        } else {
            document.exitFullscreen();
            setIsFullscreen(false);
        }
    }, [isFullscreen, setIsFullscreen, renderData])

    const inputHandles = useMemo(
        () => {
            if (inputPorts.length === 0) {
                return '';
            }
            const items = inputPorts.map((port: Port, i: number) => {
                const handleId = `input-${nodeId}-port-id-${i}`;
                const isConnected = edges.some(edge => edge.targetHandle === handleId );
                return (
                    <div key={handleId} className={`port ${isConnected ? 'connected' : 'not-connected'}`}>
                        <Handle
                            type="target"
                            position={Position.Left}
                            id={handleId}
                            isConnectable={true}
                        />
                        <div className='description'>
                            <div className='label'>{port.label}</div>
                            <div className='type'>{parseDataTypes(port.data_type)}</div>
                        </div>
                    </div>
                );
            });

            return (
                <div className='input ports'>
                    {items}
                </div>
            )
        },
        [inputPorts, nodeId, edges]
    );


    const footer = useMemo(() => {
        if (node.icon == null) {
            return (
                <footer style={{ background: color }}></footer>
            )
        }

        const iconName = node.icon as IconNames;
        const Icon = Icons[iconName];
        return (
            <footer style={{ background: color }}>
                <Tooltip title={node.description} style={{ background: color }}>
                    <Icon className='icon' />
                </Tooltip>
            </footer>
        )

    }, [node, color]);


    const handleDataType = useCallback(() => {
        if (renderData !== undefined) {
            const dtype = renderData.data_type;
            if (dtype === "Image") {
                const data = renderData.data as Image;
                const alt = `render image ${data.image_path}`
                const src = `data:image/jpeg;base64,${data.data}`
                return <img src={src} width={data.description.width} height={data.description.height} alt={alt} />
            } else if (dtype === "str") {
                const data = renderData.data as string;
                return <Typography variant='h3' align='center' sx={{ padding: 2 }}>{data}</Typography>
            } else if (dtype === "Plot") {
                const plotData = renderData.data;
                return (
                    <Plot
                        data={plotData.data}
                        layout={ plotData.layout}
                    />
                    )
            }
        }

        return <Skeleton sx={{ height: 128, width: 256 }} animation="wave" variant="rectangular" />
    }, [renderData]);

    return (
        <div className="flow-node display-node">
            <header style={{ background: color }}>
                {node.display_name}
            </header>
            <div>
                {inputHandles}
            </div>
            <div className={`display ${extraCssClasses}`} onClick={handleFullscreen}>
                {handleDataType()}
            </div>
            {footer}
        </div>
    );
};

export default RenderNode;
